/**
 * Typed client for django-allauth headless (browser) at /_allauth/browser/v1 (ARCHITECT §7).
 *
 * allauth answers 401 both for "not logged in" and for "next step needed" (verify email, 2FA code).
 * These functions return an `AuthState` for 200/401 instead of throwing, so screens can route
 * to the next step; real errors (wrong password, invalid code…) throw `ApiError`.
 */

import { ApiError } from "./errors";
import { request } from "./http";

const BASE = "/_allauth/browser/v1";

export interface AuthUser {
  id: string; // public uid
  email: string;
  display: string;
  first_name?: string;
  last_name?: string;
  is_staff?: boolean;
  has_usable_password?: boolean;
}

export type FlowId =
  | "login"
  | "signup"
  | "verify_email"
  | "mfa_authenticate"
  | "mfa_reauthenticate"
  | "reauthenticate"
  | "password_reset_by_code"
  | (string & {});

export interface AuthFlow {
  id: FlowId;
  is_pending?: boolean;
  types?: string[];
}

export interface AuthState {
  authenticated: boolean;
  user: AuthUser | null;
  /** The step the user must complete next (e.g. "mfa_authenticate"), if any. */
  pendingFlow: FlowId | null;
  flows: AuthFlow[];
}

interface AllauthBody {
  status: number;
  data?: { user?: AuthUser; flows?: AuthFlow[]; [key: string]: unknown };
  meta?: { is_authenticated?: boolean; [key: string]: unknown };
}

function toState(body: AllauthBody | null): AuthState {
  const flows = body?.data?.flows ?? [];
  return {
    authenticated: Boolean(body?.meta?.is_authenticated),
    user: body?.data?.user ?? null,
    pendingFlow: flows.find((flow) => flow.is_pending)?.id ?? null,
    flows,
  };
}

/** Requests whose 401 / 410 response describes the auth state rather than an error. */
async function authCall(path: string, init?: RequestInit): Promise<AuthState> {
  try {
    const response = await request<AllauthBody>(`${BASE}${path}`, init);
    return toState(response.data);
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 410)) {
      return toState(error.body as AllauthBody | null);
    }
    throw error;
  }
}

function json(method: string, body?: unknown): RequestInit {
  return { method, body: body === undefined ? undefined : JSON.stringify(body) };
}

// ── session ──────────────────────────────────────────────────────────

/** Who is logged in? Also makes Django set the `csrftoken` cookie used by every later POST. */
export function getSession(): Promise<AuthState> {
  return authCall("/auth/session");
}

export function login(credentials: { email: string; password: string }): Promise<AuthState> {
  return authCall("/auth/login", json("POST", credentials));
}

/** Second step of login when the account has 2FA (`pendingFlow === "mfa_authenticate"`). */
export function authenticateTwoFactor(code: string): Promise<AuthState> {
  return authCall("/auth/2fa/authenticate", json("POST", { code }));
}

export async function logout(): Promise<void> {
  await authCall("/auth/session", { method: "DELETE" });
}

// ── signup & email verification ──────────────────────────────────────

/** Name and mobile are optional extras (the storefront's registration form sends them; the vendor panel doesn't). */
export function signup(data: {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
}): Promise<AuthState> {
  return authCall("/auth/signup", json("POST", data));
}

/** `key` comes from the emailed link /verify-email/:key (React Router decodes it). */
export function verifyEmail(key: string): Promise<AuthState> {
  return authCall("/auth/email/verify", json("POST", { key }));
}

// ── passwords ────────────────────────────────────────────────────────

export async function requestPasswordReset(email: string): Promise<void> {
  await request(`${BASE}/auth/password/request`, json("POST", { email }));
}

/** Checks a reset key before showing the form (throws ApiError if invalid/expired). */
export async function checkPasswordResetKey(key: string): Promise<void> {
  await request(`${BASE}/auth/password/reset`, { headers: { "X-Password-Reset-Key": key } });
}

export function resetPassword(data: { key: string; password: string }): Promise<AuthState> {
  return authCall("/auth/password/reset", json("POST", data));
}

export async function changePassword(data: {
  current_password: string;
  new_password: string;
}): Promise<void> {
  await request(`${BASE}/account/password/change`, json("POST", data));
}

// ── two-factor authentication (TOTP) ─────────────────────────────────

export type TotpStatus =
  | { active: true; createdAt?: number; lastUsedAt?: number | null }
  | { active: false; secret: string; totpUrl: string };

/** Active TOTP details, or a fresh secret + otpauth:// URL to show as a QR code. */
export async function getTotp(): Promise<TotpStatus> {
  try {
    const { data } = await request<{ data: { created_at?: number; last_used_at?: number | null } }>(
      `${BASE}/account/authenticators/totp`,
    );
    return {
      active: true,
      createdAt: data.data.created_at,
      lastUsedAt: data.data.last_used_at ?? null,
    };
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      const meta = (error.body as { meta?: { secret?: string; totp_url?: string } } | null)?.meta;
      if (meta?.secret && meta.totp_url)
        return { active: false, secret: meta.secret, totpUrl: meta.totp_url };
    }
    throw error;
  }
}

export async function activateTotp(code: string): Promise<void> {
  await request(`${BASE}/account/authenticators/totp`, json("POST", { code }));
}

export async function deactivateTotp(): Promise<void> {
  await request(`${BASE}/account/authenticators/totp`, { method: "DELETE" });
}

export async function getRecoveryCodes(): Promise<string[]> {
  const { data } = await request<{ data: { unused_codes?: string[] } }>(
    `${BASE}/account/authenticators/recovery-codes`,
  );
  return data.data.unused_codes ?? [];
}

export async function regenerateRecoveryCodes(): Promise<string[]> {
  const { data } = await request<{ data: { unused_codes?: string[] } }>(
    `${BASE}/account/authenticators/recovery-codes`,
    json("POST"),
  );
  return data.data.unused_codes ?? [];
}
